import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import t from '@guestyci/localize/t.macro';
import {
  Button,
  Heading,
  Input,
  Text,
  Stack,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  createSchema,
  required,
} from '@guestyci/arc';
import { FormLayout } from '../../layout';
import ValidationRules from '../CustomResetPassword/ValidationRules';
import { getSessionEmail, getSessionFirstName, getSessionLastName } from '../../utils/widgetConfig';

const passwordExpiredFormSchema = createSchema({
  oldPassword: required(),
  newPassword: required(),
  confirmPassword: required(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: t('Passwords must match'),
  path: ['confirmPassword'],
});

const getTitle = ({ daysUntilExpiry, isExpiringToday, hasExpired }) => {
  if (hasExpired) {
    return t('Your password has expired');
  }
  if (typeof daysUntilExpiry === 'number' && daysUntilExpiry > 0) {
    return t('Your password will expire in {daysUntilExpiry} days', {
      daysUntilExpiry,
    });
  }
  if (isExpiringToday) {
    return t('Your password will expire later today');
  }
  return t('Your password is expiring soon');
};

const getSubtitle = ({ hasExpired }) => {
  if (hasExpired) {
    return t('Your password has expired. Please create a new one to keep your account secure and continue managing your listings.');
  }
  return t('When password expires you will be locked out of your Guesty account.');
};

const CustomPasswordExpired = ({
  passwordExpiryInfo,
  onChangePassword,
  onSignOut,
  onRemindLater,
  error: externalError,
  isSubmitting,
  userEmail: emailProp = '',
}) => {
  const {
    daysUntilExpiry,
    isExpiringToday,
    hasExpired,
    canRemindLater,
  } = passwordExpiryInfo || {};

  const email = emailProp || getSessionEmail();
  const firstName = getSessionFirstName();
  const lastName = getSessionLastName();

  const form = useForm({
    resolver: zodResolver(passwordExpiredFormSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const newPassword = form.watch('newPassword');
  const submitted = form.formState.isSubmitted;

  const handleSubmit = (data) => {
    onChangePassword({
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword,
    });
  };

  const fields = [
    {
      name: 'oldPassword',
      label: t('Old password'),
      autoComplete: 'current-password',
    },
    {
      name: 'newPassword',
      label: t('New password'),
      autoComplete: 'new-password',
    },
    {
      name: 'confirmPassword',
      label: t('Confirm new password'),
      autoComplete: 'new-password',
    },
  ];

  return (
    <FormLayout>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="gst-flex gst-flex-col gst-flex-1"
        >
          <Stack spacing={6} className="gst-flex-1 gst-pt-6 lg:gst-pt-0">
            <Stack spacing={2}>
              <Heading variant="h2">
                {getTitle({ daysUntilExpiry, isExpiringToday, hasExpired })}
              </Heading>
              <Text
                type="secondary"
                size="lg"
                className="gst-text-muted-foreground"
              >
                {getSubtitle({ hasExpired })}
              </Text>
            </Stack>

            <Stack spacing={4}>
              {fields.map(({ name, label, autoComplete }) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{label}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="password"
                          autoComplete={autoComplete}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </Stack>

            <ValidationRules
              password={newPassword}
              email={email}
              firstName={firstName}
              lastName={lastName}
              submitted={submitted}
              oktaRequirements={passwordExpiryInfo?.requirements?.items || []}
            />

            {externalError && (
              <Text type="error" size="sm">
                {externalError}
              </Text>
            )}

            <Stack spacing={3}>
              <Button
                type="submit"
                className="gst-w-full"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {t('Reset password')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="gst-w-full"
                onClick={onSignOut}
                disabled={isSubmitting}
              >
                {t('Sign out')}
              </Button>
              {canRemindLater && (
                <Stack align="center">
                  <Button
                    type="button"
                    variant="plain"
                    size="sm"
                    onClick={onRemindLater}
                    disabled={isSubmitting}
                  >
                    {t('Remind me later')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>
        </form>
      </Form>
    </FormLayout>
  );
};

export default CustomPasswordExpired;
